<script setup>
import { computed, ref, watch } from 'vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  getGroupedDynamicSources,
  getDataTypeOptions,
  getVariableTypeOptions,
  createDefaultMapping,
} from 'dashboard/helper/templateVariableHelper';

const props = defineProps({
  variables: {
    type: Array,
    required: true,
    default: () => [],
  },
  modelValue: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue']);

const variableMappings = ref({});
const openDropdowns = ref({});
const isInternalUpdate = ref(false);

// Initialize from parent modelValue once on mount
watch(
  () => props.modelValue,
  newValue => {
    if (
      !isInternalUpdate.value &&
      newValue &&
      Object.keys(newValue).length > 0
    ) {
      variableMappings.value = { ...newValue };
    }
  },
  { immediate: true }
);

// Initialize mappings when variables change
watch(
  () => props.variables,
  (newVariables, oldVariables) => {
    // Don't reinitialize if variables haven't actually changed
    if (JSON.stringify(newVariables) === JSON.stringify(oldVariables)) {
      return;
    }

    // Initialize mappings for new variables
    newVariables.forEach(position => {
      if (!variableMappings.value[position]) {
        variableMappings.value[position] = createDefaultMapping();
      }
    });

    // Remove mappings for variables that no longer exist
    Object.keys(variableMappings.value).forEach(position => {
      if (!newVariables.includes(position)) {
        delete variableMappings.value[position];
      }
    });
  },
  { immediate: true }
);

// Emit changes to parent (debounced to prevent lag)
let emitTimeout = null;
watch(
  variableMappings,
  newValue => {
    isInternalUpdate.value = true;

    if (emitTimeout) clearTimeout(emitTimeout);
    emitTimeout = setTimeout(() => {
      emit('update:modelValue', newValue);
      isInternalUpdate.value = false;
    }, 100);
  },
  { deep: true }
);

const dataTypeOptions = getDataTypeOptions();
const variableTypeOptions = getVariableTypeOptions();
const dynamicSourceGroups = getGroupedDynamicSources();

// Flatten dynamic sources for easier lookup
const allDynamicSources = computed(() => {
  return dynamicSourceGroups.flatMap(group => group.options);
});

const toggleDropdown = (variablePosition, dropdownType) => {
  const key = `${variablePosition}-${dropdownType}`;
  openDropdowns.value[key] = !openDropdowns.value[key];
};

const closeDropdown = (variablePosition, dropdownType) => {
  const key = `${variablePosition}-${dropdownType}`;
  openDropdowns.value[key] = false;
};

const isDropdownOpen = (variablePosition, dropdownType) => {
  const key = `${variablePosition}-${dropdownType}`;
  return openDropdowns.value[key] || false;
};

const handleDropdownAction = (position, dropdownType, { action, value }) => {
  // Ignore separator and header actions
  if (action === 'separator' || action === 'header') {
    return;
  }

  // Handle the actual selection
  if (dropdownType === 'type') {
    variableMappings.value[position].type = value;
    if (value === 'static') {
      // Clear source for static variables
      variableMappings.value[position].source = '';
    }
  } else if (dropdownType === 'dataType') {
    variableMappings.value[position].data_type = value;
  } else if (dropdownType === 'source') {
    variableMappings.value[position].source = value;
  }

  closeDropdown(position, dropdownType);
};

const getSelectedLabel = (value, options) => {
  const option = options.find(opt => opt.value === value);
  return option ? option.label : '';
};

const getSelectedSourceLabel = position => {
  const mapping = variableMappings.value[position];
  if (!mapping || !mapping.source) return 'Select field...';

  const option = allDynamicSources.value.find(
    opt => opt.value === mapping.source
  );
  return option ? option.label : mapping.source;
};

const createDropdownOptions = options => {
  return options.map(opt => ({
    ...opt,
    action: 'select',
  }));
};

const createGroupedDropdownOptions = groups => {
  // Flatten grouped options into single array for DropdownMenu
  const flatOptions = [];

  groups.forEach((group, groupIndex) => {
    // Add a disabled separator item for group label (except for first group)
    if (groupIndex > 0) {
      flatOptions.push({
        label: '---',
        value: `separator-${groupIndex}`,
        action: 'separator',
        disabled: true,
      });
    }

    // Add group header as disabled item
    flatOptions.push({
      label: group.label,
      value: `header-${group.label}`,
      action: 'header',
      disabled: true,
    });

    // Add group options
    group.options.forEach(opt => {
      flatOptions.push({
        ...opt,
        action: 'select',
      });
    });
  });

  return flatOptions;
};

const isVariableMapped = position => {
  const mapping = variableMappings.value[position];
  if (!mapping) return false;
  if (!mapping.type || !mapping.data_type) return false;
  if (mapping.type === 'dynamic' && !mapping.source) return false;
  return true;
};

const formatVariablePlaceholder = position => {
  return `{{${position}}}`;
};
</script>

<template>
  <div
    v-if="variables.length > 0"
    class="bg-n-solid-2 rounded-lg p-4 border border-n-weak"
  >
    <div class="flex items-center gap-3 mb-4">
      <Icon icon="i-lucide-variable" class="size-5 text-n-blue-9" />
      <h4 class="font-medium text-n-slate-12">
        Variable Mapping ({{ variables.length }} variable{{
          variables.length !== 1 ? 's' : ''
        }}
        detected)
      </h4>
    </div>

    <div class="space-y-4">
      <div
        v-for="position in variables"
        :key="position"
        class="bg-n-solid-1 rounded-lg p-4 border"
        :class="
          isVariableMapped(position) ? 'border-n-green-6' : 'border-n-amber-6'
        "
      >
        <div class="flex items-center gap-2 mb-3">
          <span class="font-mono text-sm font-semibold text-n-blue-9">
            {{ formatVariablePlaceholder(position) }}
          </span>
          <Icon
            v-if="isVariableMapped(position)"
            icon="i-lucide-check-circle"
            class="size-4 text-n-green-9"
          />
          <Icon
            v-else
            icon="i-lucide-alert-circle"
            class="size-4 text-n-amber-9"
          />
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <!-- Variable Type Dropdown -->
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              Variable Type
            </label>
            <div class="relative">
              <button
                type="button"
                class="flex items-center justify-between gap-2 w-full px-3 py-2 border border-n-weak rounded-lg bg-n-solid-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-5"
                @click="toggleDropdown(position, 'type')"
              >
                <span class="truncate">{{
                  getSelectedLabel(
                    variableMappings[position]?.type,
                    variableTypeOptions
                  )
                }}</span>
                <Icon
                  icon="i-lucide-chevron-down"
                  class="size-4 flex-shrink-0"
                />
              </button>
              <DropdownMenu
                v-if="isDropdownOpen(position, 'type')"
                :menu-items="createDropdownOptions(variableTypeOptions)"
                class="absolute top-full mt-1 left-0 right-0 w-full z-50"
                @action="
                  payload => handleDropdownAction(position, 'type', payload)
                "
              />
            </div>
          </div>

          <!-- Dynamic Source Dropdown (only for dynamic variables) -->
          <div v-if="variableMappings[position]?.type === 'dynamic'">
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              Data Source
            </label>
            <div class="relative">
              <button
                type="button"
                class="flex items-center justify-between gap-2 w-full px-3 py-2 border rounded-lg bg-n-solid-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2"
                :class="
                  variableMappings[position]?.source
                    ? 'border-n-weak focus:ring-n-blue-5'
                    : 'border-n-amber-6 focus:ring-n-amber-6'
                "
                @click="toggleDropdown(position, 'source')"
              >
                <span class="truncate">{{
                  getSelectedSourceLabel(position)
                }}</span>
                <Icon
                  icon="i-lucide-chevron-down"
                  class="size-4 flex-shrink-0"
                />
              </button>
              <DropdownMenu
                v-if="isDropdownOpen(position, 'source')"
                :menu-items="createGroupedDropdownOptions(dynamicSourceGroups)"
                class="absolute top-full mt-1 left-0 right-0 w-full z-50 max-h-64 overflow-y-auto"
                @action="
                  payload => handleDropdownAction(position, 'source', payload)
                "
              />
            </div>
          </div>

          <!-- Data Type Dropdown -->
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              Data Type
            </label>
            <div class="relative">
              <button
                type="button"
                class="flex items-center justify-between gap-2 w-full px-3 py-2 border border-n-weak rounded-lg bg-n-solid-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-5"
                @click="toggleDropdown(position, 'dataType')"
              >
                <span class="truncate">{{
                  getSelectedLabel(
                    variableMappings[position]?.data_type,
                    dataTypeOptions
                  )
                }}</span>
                <Icon
                  icon="i-lucide-chevron-down"
                  class="size-4 flex-shrink-0"
                />
              </button>
              <DropdownMenu
                v-if="isDropdownOpen(position, 'dataType')"
                :menu-items="createDropdownOptions(dataTypeOptions)"
                class="absolute top-full mt-1 left-0 right-0 w-full z-50"
                @action="
                  payload => handleDropdownAction(position, 'dataType', payload)
                "
              />
            </div>
          </div>
        </div>

        <!-- Fallback Value Input (for dynamic variables) -->
        <div
          v-if="variableMappings[position]?.type === 'dynamic'"
          class="mt-3 col-span-full"
        >
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            Fallback Value (Optional)
          </label>
          <input
            v-model="variableMappings[position].fallback_value"
            type="text"
            class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-solid-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-5"
            placeholder="Value to use if data is missing..."
          />
          <p class="mt-1 text-xs text-n-slate-10">
            This value will be used if the contact data is empty or unavailable
          </p>
        </div>

        <!-- Example Value Input (for static variables) -->
        <div
          v-if="variableMappings[position]?.type === 'static'"
          class="mt-3 col-span-full"
        >
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            Example Value (Required for approval)
          </label>
          <input
            v-model="variableMappings[position].example_value"
            type="text"
            class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-solid-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-5"
            placeholder="Example: John, 25, 2024-01-15..."
            required
          />
          <p class="mt-1 text-xs text-n-slate-10">
            <Icon
              icon="i-lucide-info"
              class="size-3 inline mr-1 text-n-blue-9"
            />
            WhatsApp requires an example value for template approval. Agent will
            provide actual value when sending.
          </p>
        </div>
      </div>
    </div>

    <!-- Validation message -->
    <div
      v-if="!variables.every(pos => isVariableMapped(pos))"
      class="mt-4 p-3 bg-n-amber-2 border border-n-amber-6 rounded-lg flex items-start gap-2"
    >
      <Icon
        icon="i-lucide-alert-triangle"
        class="size-4 text-n-amber-9 mt-0.5"
      />
      <p class="text-sm text-n-amber-11">
        Please map all variables before saving the template
      </p>
    </div>
  </div>
</template>
